<template>
	<div ref="list_selector_container" class="list-selector" :style="dynamicStyle">
		<div v-show="showOnLoading || !loading">
			<ListItemShield
				:key="item[pk]"
				:style="getItemStyle(item)"
				@click="selectItem(item)"
				@mouseover="setFocusItem(item)"
				@mouseleave="setBlurItem(item)"
				v-for="item in items"
				:ref="`list_item_${item[pk]}`"
				class="list-item-container"
				:class="{
					disabled: item.disabled,
					focused: focusedItemKey === item[pk],
					'is-selected': String(item[pk]) === String(selectedItemId),
					'has-end-hover-section': showEndHoverSectionForItem(item),
					'keyboard-navigation': allowKeyboardNavigation,
				}"
			>
				<div class="list-item-start display-flex flex-1 flex-column">
					<div class="list-item-icon" v-if="item.icon">
						<slot name="icon" v-bind:item="item">
							<SIcon :name="item.icon" />
						</slot>
					</div>
					<div class="list-item-content display-flex flex-column flex-align-start flex-justify-center flex-1">
						<slot name="item-content" v-bind:item="item">
							<div class="list-item-title">{{ item.title }}</div>
							<div class="list-item-description">{{ item.description }}</div>
							<div class="list-item-second-description">{{ item.secondDescription }}</div>
						</slot>
					</div>
				</div>
				<div class="list-item-end display-flex flex-column">
					<div class="flex-1 display-flex">
						<div class="list-item-tooltip display-flex">
							<slot class="list-item-tooltip" name="tooltip" v-bind:item="item" />
						</div>
						<div v-if="showEndHoverSectionForItem(item)" class="list-item-end-hover-area height-100 flex-start--center">
							<slot name="end-hover" v-bind:item="item" />
						</div>
					</div>
				</div>
			</ListItemShield>
			<div v-if="items.length === 0" class="empty-text">
				<slot name="no-data">{{ emptyText }} </slot>
			</div>
		</div>
		<div v-if="loading"><Loader /></div>
	</div>
</template>

<script>
import { Key } from 'ts-key-enum';

import ListItemShield from './ListItemShield.vue';
import Loader from '@/components/common/Loader';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
export default {
	name: 'ListSelector',
	props: {
		items: { type: Array },
		selectedItemId: { type: [String, Number] },
		emptyText: { type: String },
		pk: { type: String, default: 'id' },
		itemStyle: { type: Object },
		maxHeight: { type: String },
		returnObject: { type: Boolean },
		loading: { type: Boolean },
		showOnLoading: { type: Boolean },
		allowKeyboardNavigation: { type: Boolean },
		autoSelectFirst: { type: Boolean },
		autoFocus: { type: Boolean },
		showEndHoverAreaKey: { type: String }, // key of value in item that decides rather or not show end hover area section
	},
	components: { Loader, ListItemShield },
	computed: {
		dynamicStyle() {
			const style = {};
			if (this.maxHeight) {
				style.maxHeight = this.maxHeight;
				style.overflowY = 'auto';
			}
			return style;
		},
		focusedItem() {
			return this.items.find((item) => item[this.pk] === this.focusedItemKey);
		},
		focusedItemIndex() {
			return this.items.findIndex((item) => item[this.pk] === this.focusedItemKey);
		},
		currentFocusedItemIsLast() {
			return this.focusedItemIndex === this.items.length - 1;
		},
		currentFocusedItemIsFirst() {
			return this.focusedItemIndex === 0;
		},
		focusedItemRef() {
			return this.getCurrentFocusedItemRef();
		},
	},
	methods: {
		selectItem(item) {
			const valueToEmit = this.returnObject ? item : item[this.pk];
			this.$emit('selectItem', valueToEmit);
		},
		onEnter() {
			this.selectFocusedItem();
		},
		selectFocusedItem() {
			this.selectItem(this.focusedItem);
		},
		onKeyDown(e) {
			const LETTERS_HANDLERS = {};
			if (this.showEndHoverSectionForItem(this.focusedItem)) {
				const endHoverSectionClickedHandler = () => this.$emit('end-hover-section-clicked', this.focusedItemKey);
				LETTERS_HANDLERS['d'] = endHoverSectionClickedHandler;
				LETTERS_HANDLERS['D'] = endHoverSectionClickedHandler;
				LETTERS_HANDLERS[' '] = endHoverSectionClickedHandler;
			}
			const KEY_DOWN_HANDLER = {
				...LETTERS_HANDLERS,
				[Key.ArrowDown]: this.focusOnNextItem,
				[Key.ArrowUp]: this.focusOnPreviousItem,
				[Key.Enter]: this.onEnter,
			};
			if (KEY_DOWN_HANDLER[e.key]) {
				KEY_DOWN_HANDLER[e.key]();
				e.stopPropagation();
			}
		},
		focusCurrentFocusedItem() {
			if (this.focusedItemRef) {
				this.ignoreMouseEvents = true;
				this.focusedItemRef.scrollIntoView({ block: 'center' });
				// in order to prevent us from refocusing falsy on hover/blur browser event, we're waiting for next animation frame before clearing this flag
				window.requestAnimationFrame(() => {
					this.ignoreMouseEvents = false;
				});
			}
		},
		focusOnFirstItem() {
			this.focusedItemKey = this.items[0][this.pk];
		},
		focusOnLastItem() {
			this.focusedItemKey = this.items.at(-1)[this.pk];
		},
		focusOnNextItem() {
			if (!this.items?.length) {
				return;
			}
			if (!this.focusedItem || this.currentFocusedItemIsLast) {
				this.focusOnFirstItem();
			} else {
				this.focusedItemKey = this.items[this.focusedItemIndex + 1][this.pk];
			}
			this.focusCurrentFocusedItem();
		},
		focusOnPreviousItem() {
			if (!this.items?.length) {
				return;
			}
			if (!this.focusedItem || this.currentFocusedItemIsFirst) {
				this.focusOnLastItem();
			} else {
				this.focusedItemKey = this.items[this.focusedItemIndex - 1][this.pk];
			}
			this.focusCurrentFocusedItem();
		},
		removeOnKeyDownListener() {
			if (this.onKeyDownHandler) {
				window.removeEventListener('keydown', this.onKeyDownHandler, true);
				this.onKeyDownHandler = null;
			}
		},
		listenOnKeyDown() {
			this.removeOnKeyDownListener();
			this.onKeyDownHandler = (e) => this.onKeyDown(e);
			window.addEventListener('keydown', this.onKeyDownHandler, true);
		},
		getElement(refElement) {
			return refElement.$el;
		},
		showEndHoverSectionForItem(item) {
			return this.showEndHoverAreaKey && !!item?.[this.showEndHoverAreaKey];
		},
		getCurrentFocusedItemRef() {
			const itemRef = `list_item_${this.focusedItemKey}`;
			return this.getElement(this.$refs[itemRef][0]);
		},
		focusOnFirstItemIfCan() {
			this.items?.length && this.focusOnFirstItem();
		},
		setFocusItem(item) {
			if (this.ignoreMouseEvents) {
				// we can get here if we faked scrolled element into view focusCurrentFocusedItem and mouse falsy think it was "hover" other element
				return;
			}
			this.focusedItemKey = item[this.pk];
		},
		setBlurItem(item) {
			if (this.ignoreMouseEvents) {
				// we can get here if we faked scrolled element into view focusCurrentFocusedItem and mouse falsy think it was "blur" other element
				return;
			}
			if (this.focusedItemKey === item[this.pk]) {
				this.focusedItemKey = null;
			}
		},
		getItemStyle(item) {
			return { ...this.itemStyle, ...item.style };
		},
	},
	mounted() {
		if (this.allowKeyboardNavigation) {
			this.listenOnKeyDown();
		}
		if (this.autoFocus || this.autoSelectFirst) {
			this.$nextTick(() => {
				this.focusOnFirstItemIfCan();
				this.autoSelectFirst && this.selectItem();
			});
		}
	},
	beforeDestroy() {
		this.removeOnKeyDownListener();
	},
	data() {
		return {
			focusedItemKey: null,
			onKeyDownHandler: null,
			ignoreMouseEvents: false,
		};
	},
	watch: {
		items() {
			const currentFocusedItemNotAvailable = !this.focusedItem && !isNullOrUndefinedOrEmpty(this.focusedItemKey);
			if (currentFocusedItemNotAvailable) {
				this.focusOnFirstItemIfCan();
			} else if (this.focusedItemKey) {
				this.$nextTick(() => {
					this.focusCurrentFocusedItem();
				});
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/styles/utility-classes/backgrounds.scss';
@import './list-item.scss';
.list-selector {
	text-align: left;

	.list-item-container {
		margin-bottom: 15px;
		transition: 0.4s;
		justify-content: flex-start;

		&:not(.keyboard-navigation):hover,
		&.focused,
		&.is-selected {
			@extend .hover-background;
			cursor: pointer;
			transition: 0.2s;
		}
		&.disabled {
			cursor: default;
			opacity: 0.4;
			pointer-events: none;
		}
		.list-item-start {
			align-self: stretch;
			.list-item-icon {
				margin: 0 20px 0 10px;
			}
			.list-item-content {
				height: 100%;
				.list-item-title {
					font-size: 14px;
					color: var(--primary-font-color);
				}

				.list-item-description {
					font-size: 11px;
					color: #2626267e;
				}
				.list-item-second-description {
					font-size: 11px;
					color: grey;
					opacity: 0.7;
				}
			}
		}
		.list-item-end {
			align-self: stretch;
			.list-item-end-hover-area {
				height: 100%;
				display: none;
				opacity: 0;
				background-color: var(--white);
				border-top-right-radius: 10px;
				border-bottom-right-radius: 10px;
				border-left: 1px solid var(--button-border);
				margin-left: 9px;
				&:hover {
					transition: all 0.2s;
					@extend .hover-background;
				}
			}
		}
		&.has-end-hover-section {
			&:not(.keyboard-navigation):hover,
			&.focused {
				padding-right: 0px;
				.list-item-end {
					.list-item-end-hover-area {
						display: flex;
						opacity: 1;
					}
				}
			}
		}
	}

	.empty-text {
		text-align: center;
		opacity: 0.5;
		margin: 4px 0 10px 0;
		font-size: 14px;
	}
}
</style>
